import type { Stage } from '../../../shared/types';
import { STAGE_LABELS, REVIEW_STAGES, ACTIVE_STAGES } from '../../../shared/types';

/**
 * One badge for the single campaign stage.
 *
 * v1 showed two independent status columns that could disagree — the live sheet
 * has a campaign reading "Reviewing" and "Published" at the same time. There is
 * one state now, so there is one badge.
 */

const TONE: Record<string, string> = {
  waiting: 'bg-natural-rose text-natural-rosetext',
  working: 'bg-natural-sage/15 text-natural-sage',
  done: 'bg-emerald-50 text-emerald-700',
  bad: 'bg-red-50 text-red-600',
  idle: 'bg-stone-100 text-natural-text/55',
};

export function stageTone(stage: Stage): keyof typeof TONE {
  if (stage === 'done') return 'done';
  if (stage === 'failed' || stage === 'cancelled' || stage === 'needs_attention') return 'bad';
  if (REVIEW_STAGES.includes(stage)) return 'waiting';
  if (ACTIVE_STAGES.includes(stage)) return 'working';
  return 'idle';
}

export default function StageBadge({
  stage,
  className = '',
}: {
  stage: Stage;
  className?: string;
}) {
  const tone = stageTone(stage);
  const working = tone === 'working';

  return (
    <span className={`zen-pill ${TONE[tone]} ${className}`}>
      {working && <span className="h-1.5 w-1.5 rounded-full bg-current animate-pulse" />}
      {tone === 'waiting' && <span className="h-1.5 w-1.5 rounded-full bg-current" />}
      {STAGE_LABELS[stage] ?? stage}
    </span>
  );
}
